import { Injectable } from "@angular/core";
import { ICartService } from "@core-interfaces/services/cart";
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, combineLatest, map, Observable } from "rxjs";
import { StorageKeys } from "@core-interfaces/global";
import { TcreateOrderItem } from "@core-ports/inputs/orderItem";
import { IDiscount } from "@core-ports/outputs/discount";
import { AnalyticsService } from "./analytics.service";
import { AppEvents } from "@core-interfaces/global/events";

@Injectable({
    providedIn: 'root'
})
export class CartService implements ICartService {

    cartItems$: BehaviorSubject<TcreateOrderItem[]> = new BehaviorSubject<TcreateOrderItem[]>([]);
    originalPrice$= this.cartItems$.pipe(
        map(items => items.reduce((acc, item) => acc + (item.compare_at_price * item.quantity), 0))
    );
    baseSubtotal$ = this.cartItems$.pipe(
        map(items => items.reduce((acc, item) => acc + (item.price * item.quantity), 0))
    );
    discount$ = new BehaviorSubject<IDiscount | null>(null);
    total$: Observable<number>;

    constructor(
        private analyticsService: AnalyticsService
    ) {
        this.total$ = combineLatest([
            this.baseSubtotal$,
            this.discount$
        ]).pipe(
            map(([subtotal, discount]) => {
                if (!subtotal) return 0;
                if (!discount) return subtotal;
                return Math.max(0, subtotal - discount.discountValue);
            })
        );
    }

    async loadCartItems(): Promise<void> {
        const { value } = await Preferences.get({ key: StorageKeys.cart });
        if (value) {
            const items: TcreateOrderItem[] = JSON.parse(value);
            this.cartItems$.next(items);
        }
    }

    async clearCart(): Promise<void> {
        console.log('Clearing cart...');
        this.cartItems$.next([]);
        await Preferences.remove({ key: StorageKeys.cart });
        this.discount$.next(null);
    }

    async removeFromCart(variantId: string): Promise<void> {
        const currentItems = this.cartItems$.getValue();
        const updatedItems = currentItems.filter(i => i.shopify_variant_id !== variantId);
        this.cartItems$.next(updatedItems);
        await Preferences.set({ key: StorageKeys.cart, value: JSON.stringify(updatedItems) });
        this.analyticsService.logEvent(AppEvents.product_remove_from_cart, {
            variant_removed_id: variantId
        });
    }

    async updateItemQuantity(variantId: string, newQuantity: number): Promise<void> {
        const currentItems = this.cartItems$.getValue();
        const updatedItems = currentItems.map(item =>
            item.shopify_variant_id === variantId
                ? { ...item, quantity: newQuantity }
                : item
        );
        this.cartItems$.next(updatedItems);
        await Preferences.set({ key: StorageKeys.cart, value: JSON.stringify(updatedItems) });
        this.analyticsService.logEvent(AppEvents.product_update_in_cart, {
            variant_updated_id: variantId,
            new_quantity: newQuantity
        });
    }

    async addAndUpdate(orderItem: TcreateOrderItem, newQuantity: number): Promise<void> {
        const currentItems = this.cartItems$.getValue();
        const itemExists = currentItems.some(item => item.shopify_variant_id === orderItem.shopify_variant_id);
        if (itemExists) {
            await this.updateItemQuantity(orderItem.shopify_variant_id, newQuantity);
        } else {
            await this.addToCart(orderItem);
        }
    }

    getProductQuantity(variantId: string): number {
        const currentItems = this.cartItems$.getValue();
        const item = currentItems.find(i => i.shopify_variant_id === variantId);
        return item ? item.quantity : 1;
    }

    setDiscount(discount: IDiscount | null): void {
        this.discount$.next(discount);
    }

    private async addToCart(item: TcreateOrderItem): Promise<void> {
        const currentItems = this.cartItems$.getValue();
        const itemIndex = currentItems.findIndex(i => i.shopify_variant_id === item.shopify_variant_id);
        if (itemIndex !== -1) {
            return;
        }
        const updatedItems = [...currentItems, item];
        this.cartItems$.next(updatedItems);
        await Preferences.set({ key: StorageKeys.cart, value: JSON.stringify(updatedItems) });

        this.analyticsService.logEvent(AppEvents.product_add_to_cart, {
            item_id: item.shopify_product_id,
            item_name: item.product_title,
            item_variant: item.variant_title,
            quantity: item.quantity,
            price: item.price,
            currency: 'CLP',
        });
    }
}
